import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translateCurrentPath } from '../utils/navigation';

interface SEOProps {
  title: string;
  description: string;
  canonicalPath: string;
}

export function useSEO({ title, description, canonicalPath }: SEOProps) {
  const { lang } = useLanguage();

  useEffect(() => {
    // 1. Update Title
    document.title = title;

    // 2. Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // 3. Update Canonical URL with guaranteed trailing slash
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    const cleanPath = canonicalPath.trim();
    const normalizedPath = cleanPath === '/' || cleanPath === ''
      ? '/'
      : (cleanPath.endsWith('/') ? cleanPath : `${cleanPath}/`);
    const fullCanonical = normalizedPath === '/' 
      ? 'https://cancunmexico.travel/' 
      : `https://cancunmexico.travel${normalizedPath}`;
    canonicalLink.setAttribute('href', fullCanonical);

    // 4. Update hreflang tags with guaranteed trailing slash
    const updateHreflang = (hl: string, href: string) => {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${hl}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hl);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    const esRoute = translateCurrentPath(normalizedPath, 'es');
    const enRoute = translateCurrentPath(normalizedPath, 'en');
    const ptRoute = translateCurrentPath(normalizedPath, 'pt');

    updateHreflang('es', `https://cancunmexico.travel${esRoute}`);
    updateHreflang('en', `https://cancunmexico.travel${enRoute}`);
    updateHreflang('pt', `https://cancunmexico.travel${ptRoute}`);
    updateHreflang('x-default', `https://cancunmexico.travel${esRoute}`);

    // 5. Update OpenGraph & Twitter tags
    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setTwitterMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:url', fullCanonical);
    setTwitterMeta('twitter:title', title);
    setTwitterMeta('twitter:description', description);
  }, [title, description, canonicalPath, lang]);
}
